import { Box, useTheme } from "@mui/material";

import {
  DETAIL_TAB,
  EViewerMode,
  ProjectDetailContext,
} from "../../../../pages/ProjectDetail";
import {
  CalculationIcon,
  HelmertIcon,
  RemoveIcon,
  UploadIcon,
} from "../../../Parts/Q3DIcons";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import Q3DIconButton from "../../../Parts/Q3DIconButton";

type Props = {
  value: EViewerMode;
  onChange: (value: EViewerMode) => void;
  disabled?: boolean;
};

const getCommonSx = (
  currentViewerMode: EViewerMode,
  mode: EViewerMode,
  theme: any
) => ({
  ...(currentViewerMode === mode
    ? {
        background: theme.palette.base.ymdBackground,
        color: "#FFFFFF",
      }
    : {
        background: "#00000000",
        color: theme.palette.base.contrast,
      }),
  border: `1px solid ${theme.palette.base.border} !important`,
  padding: "0px",
  whiteSpace: "nowrap",
  borderRadius: "0px",
  minWidth: "36px",
});

const ModeToggleButton = (props: Props) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { currentViewerMode, detailTab, updateCurrentPointCloud } =
    useContext(ProjectDetailContext);
  const strokeColor = (() => {
    if (currentViewerMode === EViewerMode.HELMERT_TRANSFORM) {
      return theme.palette.base.ymdBackground;
    } else if (theme.palette.mode === "dark") {
      return "#000000";
    } else {
      return "#ffffff";
    }
  })();
  const hoverColor = (() => {
    return currentViewerMode === EViewerMode.HELMERT_TRANSFORM
      ? "#0064BA"
      : "#5D5D5F";
  })();

  return (
    <Box sx={{ display: "flex" }}>
      <Q3DIconButton
        title={t("MEASUREMENT")}
        titleType={i18n.language === "ja" ? "both" : "tooltip"}
        disabled={props.disabled}
        disabledHoverAnimation={currentViewerMode === EViewerMode.CALCULATION}
        icon={
          <CalculationIcon
            width={20}
            height={20}
            color={currentViewerMode === EViewerMode.CALCULATION && "white"}
          />
        }
        height={"36px"}
        onClick={() => {
          props.onChange(
            currentViewerMode === EViewerMode.CALCULATION
              ? EViewerMode.DEFAULT
              : EViewerMode.CALCULATION
          );
        }}
        sx={getCommonSx(currentViewerMode, EViewerMode.CALCULATION, theme)}
      />
      <Q3DIconButton
        title={t("COORDINATE_TRANSFOR")}
        titleType={i18n.language === "ja" ? "both" : "tooltip"}
        disabled={props.disabled}
        disabledHoverAnimation={
          currentViewerMode === EViewerMode.HELMERT_TRANSFORM
        }
        tooltipHidden={detailTab === DETAIL_TAB.SIMPLE}
        icon={
          <HelmertIcon
            width={20}
            height={20}
            color={
              currentViewerMode === EViewerMode.HELMERT_TRANSFORM && "white"
            }
          />
        }
        height={"36px"}
        onClick={() => {
          props.onChange(
            currentViewerMode === EViewerMode.HELMERT_TRANSFORM
              ? EViewerMode.DEFAULT
              : EViewerMode.HELMERT_TRANSFORM
          );
        }}
        sx={{
          ...getCommonSx(
            currentViewerMode,
            EViewerMode.HELMERT_TRANSFORM,
            theme
          ),
          visibility: detailTab === DETAIL_TAB.SIMPLE ? "hidden" : "",
          "svg > .border": {
            fill:
              currentViewerMode === EViewerMode.HELMERT_TRANSFORM
                ? theme.palette.base.ymdBackground
                : "#000000",
          },
          "svg > .grid-line": {
            stroke: strokeColor,
          },
          "&:hover svg > .border": {
            fill: hoverColor,
          },
          "&:hover svg > .grid-line": {
            stroke: hoverColor,
          },
        }}
      />
      <Q3DIconButton
        title={t("REMOVE_POINT_GROUP")}
        titleType={i18n.language === "ja" ? "both" : "tooltip"}
        disabled={props.disabled}
        disabledHoverAnimation={currentViewerMode === EViewerMode.POINT_REMOVE}
        tooltipHidden={detailTab === DETAIL_TAB.SIMPLE}
        icon={
          <RemoveIcon
            width={20}
            height={20}
            color={currentViewerMode === EViewerMode.POINT_REMOVE && "white"}
          />
        }
        height={"36px"}
        onClick={() => {
          updateCurrentPointCloud(null);
          props.onChange(
            currentViewerMode === EViewerMode.POINT_REMOVE
              ? EViewerMode.DEFAULT
              : EViewerMode.POINT_REMOVE
          );
        }}
        sx={{
          ...getCommonSx(currentViewerMode, EViewerMode.POINT_REMOVE, theme),
          visibility: detailTab === DETAIL_TAB.SIMPLE ? "hidden" : "",
        }}
      />
      <Q3DIconButton
        title={t("UPLOAD_POINT_CLOUD")}
        titleType={i18n.language === "ja" ? "both" : "tooltip"}
        disabled={props.disabled}
        disabledHoverAnimation={
          currentViewerMode === EViewerMode.EXPORT_TO_OTHER
        }
        tooltipHidden={detailTab === DETAIL_TAB.SIMPLE}
        icon={
          <UploadIcon
            width={24}
            height={24}
            color={currentViewerMode === EViewerMode.EXPORT_TO_OTHER && "white"}
          />
        }
        height={"36px"}
        onClick={() => {
          props.onChange(
            currentViewerMode === EViewerMode.EXPORT_TO_OTHER
              ? EViewerMode.DEFAULT
              : EViewerMode.EXPORT_TO_OTHER
          );
        }}
        sx={{
          ...getCommonSx(currentViewerMode, EViewerMode.EXPORT_TO_OTHER, theme),
          visibility: detailTab === DETAIL_TAB.SIMPLE ? "hidden" : "",
        }}
      />
    </Box>
  );
};

export default ModeToggleButton;
